import { Bold, Italic, List, ListOrdered, Underline } from 'lucide-react';
import { useEffect, useRef } from 'react';

// A small rich-text editor for lesson plans (#186) and the journal (#187): bold, italic, underline and
// lists on a contentEditable area. No dependency; the server sanitizes whatever HTML this produces down to
// a formatting allowlist (richtext.ts), so what's stored and shown can't carry scripts or styles.

const TOOLS = [
  { cmd: 'bold', icon: <Bold size={15} />, label: 'Bold' },
  { cmd: 'italic', icon: <Italic size={15} />, label: 'Italic' },
  { cmd: 'underline', icon: <Underline size={15} />, label: 'Underline' },
  { cmd: 'insertUnorderedList', icon: <List size={15} />, label: 'Bulleted list' },
  { cmd: 'insertOrderedList', icon: <ListOrdered size={15} />, label: 'Numbered list' },
] as const;

export function RichTextEditor({
  value,
  onChange,
  label,
  placeholder,
  minHeight = 110,
}: {
  value: string;
  onChange: (html: string) => void;
  label: string;
  placeholder?: string;
  minHeight?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Only push `value` into the DOM when it differs, so typing doesn't reset the caret.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value;
  }, [value]);

  const run = (cmd: string) => {
    ref.current?.focus();
    document.execCommand(cmd);
    onChange(ref.current?.innerHTML ?? '');
  };

  return (
    <div className="richtext">
      <div className="richtext-label">{label}</div>
      <div className="richtext-toolbar" role="toolbar" aria-label={`${label} formatting`}>
        {TOOLS.map((t) => (
          <button
            key={t.cmd}
            type="button"
            className="icon-btn"
            title={t.label}
            aria-label={t.label}
            onMouseDown={(e) => e.preventDefault()} // keep the selection in the editor
            onClick={() => run(t.cmd)}
          >
            {t.icon}
          </button>
        ))}
      </div>
      <div
        ref={ref}
        className="richtext-area"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        data-placeholder={placeholder ?? ''}
        style={{ minHeight }}
        onInput={(e) => onChange((e.target as HTMLDivElement).innerHTML)}
        suppressContentEditableWarning
      />
    </div>
  );
}

/** Show stored rich text. The HTML has already been sanitized server-side to formatting tags only. */
export function RichTextView({ html, empty = '—' }: { html: string; empty?: string }) {
  if (!html) return <p className="muted small">{empty}</p>;
  return <div className="richtext-view" dangerouslySetInnerHTML={{ __html: html }} />;
}
